import { useEffect, useMemo, useRef } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import { Button } from '../../../Common/Button'
import { FormBuilder } from '../../../Forms/FormBuilder'
import type { FormDefinition, FormField, FormFieldType, FormTemplate } from '../../../../types/forms'
import {
  type FormPayloadField,
  type FormPayload,
  normalizeFormPayload,
} from './formPayload'

export type { FormPayloadField, FormPayload }

type FormPayloadEditorProps = {
  value: FormPayload
  onChange: (next: FormPayload) => void
}

const FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'select', 'checkbox', 'file', 'person'] as const

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? value as Record<string, unknown> : {}

const asString = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback

const normalizeOptions = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []

const parseOptions = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)

const serializeOptions = (value: unknown) => normalizeOptions(value).map((option) => typeof option === 'string' ? option : asString(asRecord(option).label, '')).filter(Boolean).join(', ')

const reorder = <T,>(items: T[], fromIndex: number, toIndex: number): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, moved)
  return next
}

const toBuilderFieldType = (value: unknown): FormFieldType => {
  const raw = asString(value, 'text').toLowerCase()
  if (raw === 'number') return 'NUMBER'
  if (raw === 'date') return 'DATE'
  if (raw === 'user' || raw === 'person' || raw === 'member') return 'PERSON'
  if (raw === 'select' || raw === 'dropdown' || raw === 'radio') return 'DROPDOWN'
  if (raw === 'checkbox' || raw === 'boolean') return 'CHECKBOX'
  if (raw === 'file' || raw === 'upload') return 'FILE_UPLOAD'
  return 'TEXT'
}

const toBuilderField = (field: FormPayloadField, index: number): FormField => {
  const id = asString(field.id, `field-${index + 1}`)
  const label = asString(field.label, `Field ${index + 1}`)
  const required = field.required === true
  const order = index
  const type = toBuilderFieldType(field.type)
  if (type === 'PERSON') {
    return {
      id,
      label,
      required,
      order,
      type: 'PERSON',
      allowMultiple: field.allowMultiple === true,
      validation: {},
    }
  }
  if (type === 'DROPDOWN') {
    const options = normalizeOptions(field.options)
    return {
      id,
      label,
      required,
      order,
      type: 'DROPDOWN',
      placeholder: asString(field.placeholder, 'Select an option'),
      presentation: 'SELECT',
      options: (options.length > 0 ? options : ['Option 1']).map((option, optionIndex) => {
        const optionLabel = typeof option === 'string' ? option : asString(asRecord(option).label, `Option ${optionIndex + 1}`)
        return { id: `${id}-option-${optionIndex + 1}`, label: optionLabel, value: optionLabel.toLowerCase().replace(/\s+/g, '-') }
      }),
      validation: {},
    }
  }
  if (type === 'CHECKBOX') return { id, label, required, order, type: 'CHECKBOX', checkboxLabel: label, validation: {} }
  if (type === 'DATE') return { id, label, required, order, type: 'DATE', validation: {} }
  if (type === 'NUMBER') return { id, label, required, order, type: 'NUMBER', placeholder: asString(field.placeholder, ''), validation: {} }
  return {
    id,
    label,
    required,
    order,
    type: 'TEXT',
    placeholder: asString(field.placeholder, ''),
    multiline: asString(field.type).toLowerCase() === 'textarea',
    validation: {},
  }
}

const fromBuilderField = (field: FormField): FormPayloadField => {
  const base: FormPayloadField = {
    id: field.id,
    type: field.type === 'TEXT' && field.multiline ? 'textarea' : field.type.toLowerCase().replace('dropdown', 'select').replace('file_upload', 'file'),
    label: field.label,
    required: field.required,
  }
  if ('placeholder' in field && field.placeholder) base.placeholder = field.placeholder
  if (field.type === 'DROPDOWN') base.options = field.options.map((option) => option.label)
  return base
}

const toBuilderTemplate = (payload: FormPayload): FormTemplate => ({
  id: asString(payload.formTemplateId, 'owner-template-payload'),
  workspaceId: null,
  scope: 'GLOBAL',
  title: 'Template payload',
  description: '',
  category: 'Owner templates',
  fields: payload.fields.map(toBuilderField),
  logic: { version: 1, rules: payload.logic ?? [] },
  theme: {},
  createdAt: null,
  updatedAt: null,
})

export const FormPayloadEditor = ({ value, onChange }: FormPayloadEditorProps) => {
  const emittedInitialValueRef = useRef(false)
  const skippedBuilderHydrationRef = useRef(false)
  const draggedFieldIndexRef = useRef<number | null>(null)
  const payload = useMemo(() => normalizeFormPayload(value), [value])
  const builderTemplate = useMemo(() => toBuilderTemplate(payload), [payload])

  useEffect(() => {
    if (emittedInitialValueRef.current) return
    emittedInitialValueRef.current = true
    onChange(payload)
  }, [onChange, payload])

  const emit = (next: FormPayload) => onChange(normalizeFormPayload(next))

  const updateField = (fieldIndex: number, patch: Partial<FormPayloadField>) => {
    emit({
      ...payload,
      fields: payload.fields.map((field, index) => index === fieldIndex ? { ...field, ...patch } : field),
    })
  }

  const addField = () => emit({
    ...payload,
    fields: [...payload.fields, {
      id: `field-${payload.fields.length + 1}`,
      type: 'text',
      label: 'New field',
      required: false,
    }],
  })

  const deleteField = (fieldIndex: number) => emit({
    ...payload,
    fields: payload.fields.filter((_, index) => index !== fieldIndex),
  })

  const reorderField = (toIndex: number) => {
    const fromIndex = draggedFieldIndexRef.current
    draggedFieldIndexRef.current = null
    if (fromIndex === null) return
    emit({ ...payload, fields: reorder(payload.fields, fromIndex, toIndex) })
  }

  const handleBuilderChange = (definition: FormDefinition) => {
    if (!skippedBuilderHydrationRef.current) {
      skippedBuilderHydrationRef.current = true
      return
    }
    emit({
      ...payload,
      fields: definition.fields.map(fromBuilderField),
      ...(definition.logic.rules.length > 0 ? { logic: definition.logic.rules } : {}),
    })
  }

  return (
    <section className="formPayloadEditor" aria-label="Form payload editor">
      <div className="formPayloadEditor__fields">
        {payload.fields.map((field, fieldIndex) => (
          <article
            className="formPayloadEditor__field"
            key={`${asString(field.id, 'field')}-${fieldIndex}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => reorderField(fieldIndex)}
          >
            <button
              type="button"
              className="formPayloadEditor__drag"
              draggable
              aria-label={`Drag field ${fieldIndex + 1}`}
              onDragStart={() => { draggedFieldIndexRef.current = fieldIndex }}
            >
              <DragIndicatorRoundedIcon fontSize="inherit" />
            </button>
            <label>
              <span>ID</span>
              <input
                aria-label={`Field ${fieldIndex + 1} id`}
                value={asString(field.id)}
                onChange={(event) => updateField(fieldIndex, { id: event.target.value })}
              />
            </label>
            <label>
              <span>Type</span>
              <select
                aria-label={`Field ${fieldIndex + 1} type`}
                value={asString(field.type, 'text')}
                onChange={(event) => updateField(fieldIndex, { type: event.target.value })}
              >
                {FIELD_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </label>
            <label>
              <span>Label</span>
              <input
                aria-label={`Field ${fieldIndex + 1} label`}
                value={asString(field.label)}
                onChange={(event) => updateField(fieldIndex, { label: event.target.value })}
              />
            </label>
            <label className="formPayloadEditor__required">
              <span>Required</span>
              <input
                aria-label={`Field ${fieldIndex + 1} required`}
                type="checkbox"
                checked={field.required === true}
                onChange={(event) => updateField(fieldIndex, { required: event.target.checked })}
              />
            </label>
            <label>
              <span>Options</span>
              <input
                aria-label={`Field ${fieldIndex + 1} options`}
                value={serializeOptions(field.options)}
                onChange={(event) => updateField(fieldIndex, { options: parseOptions(event.target.value) })}
              />
            </label>
            <button
              type="button"
              className="formPayloadEditor__iconButton"
              aria-label={`Delete field ${fieldIndex + 1}`}
              onClick={() => deleteField(fieldIndex)}
            >
              <DeleteRoundedIcon fontSize="inherit" />
            </button>
          </article>
        ))}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        icon={<AddRoundedIcon fontSize="inherit" />}
        onClick={addField}
      >
        Add field
      </Button>
      <div className="formPayloadEditor__builder">
        <FormBuilder
          key={JSON.stringify(payload.fields.map((field) => [field.id, field.type, field.label, field.required]))}
          template={builderTemplate}
          persistMode="callback"
          onClose={() => undefined}
          onSaved={() => undefined}
          onDirtyChange={() => undefined}
          onDefinitionChange={handleBuilderChange}
        />
      </div>
    </section>
  )
}
